import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, Compass } from "lucide-react";

/**
 * Volunteer "Getting started" checklist — mirror of OrgStarterChecklist.
 * The server decides WHO sees it (GET /me/starter-checklist: individuals
 * only, no canonical demo accounts, account < 30 days old) and auto-checks
 * steps from real data. Item 4 (2026-07): the fetch is hoisted to
 * VolunteerHome, which swaps this in PLACE of the My community / My impact /
 * My activity modules on a true first visit (no applications, no hours).
 */
const STEP_META = [
  { id: "profile", label: "Complete your profile", to: "/onboarding", cta: "Finish profile" },
  { id: "application", label: "Apply for your first opportunity — or offer help to a neighbour", to: "/opportunities", cta: "Find opportunities" },
  { id: "hours", label: "Log your first volunteer hours", to: "/log-hours", cta: "Log hours" },
  { id: "collective", label: "Join a collective near you", to: "/collectives", cta: "Find yours" },
  { id: "story", label: "Share your first story", to: "/stories", cta: "Visit stories" },
];

export function VolunteerStarterChecklist({ user, data, onDismiss }) {
  const dismissKey = `vol_checklist_dismissed_${user.user_id}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey) === "1");

  if (dismissed || !data?.show) return null;
  const steps = STEP_META.map((m) => ({ ...m, done: !!data.steps[m.id] }));
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;

  return (
    <div data-testid="volunteer-starter-checklist" className="mt-10 bg-white border border-terracotta/25 rounded-2xl p-6 relative">
      <button
        data-testid="vol-checklist-dismiss"
        aria-label="Dismiss checklist"
        onClick={() => { localStorage.setItem(dismissKey, "1"); setDismissed(true); onDismiss?.(); }}
        className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2">
        <Compass className="w-5 h-5 text-terracotta" />
        <div className="font-display text-xl text-forest">Getting started</div>
        <span data-testid="vol-checklist-progress" className="text-xs text-neutral-500 ml-1">{doneCount} of {steps.length} done</span>
      </div>
      <div className="mt-4 space-y-2.5">
        {steps.map((s) => (
          <div key={s.id} data-testid={`vol-checklist-step-${s.id}`} data-done={s.done} className="flex items-start gap-3 text-sm">
            {s.done
              ? <CheckCircle2 className="w-[18px] h-[18px] text-forest shrink-0 mt-0.5" />
              : <Circle className="w-[18px] h-[18px] text-neutral-300 shrink-0 mt-0.5" />}
            <span className={s.done ? "text-neutral-400 line-through" : "text-neutral-800"}>{s.label}</span>
            {!s.done && (
              <Link to={s.to} data-testid={`vol-checklist-link-${s.id}`} className="text-xs mt-0.5 text-forest underline shrink-0">
                {s.cta}
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
